import { MutableRefObject, useEffect } from 'react'
import moment from 'moment'
import { DataGridProProps as MUIDataGridProps } from '@microservices/wiskey-react-table-component'
import { GridApiPro } from '@microservices/wiskey-react-table-component/dist/DataGrid/DataGridV7/models/gridApiPro'

import { useHandleColumnDrag } from '@pages/ConfiguredEntity/hooks'

import { ACTION_CODE, EVENT_CODE, MAIN_OBJECT_TYPE_X } from '@constants'
import {
  ApiRestrictionData,
  GETVariableDTO,
  POSTObjectDataFilter,
  SearchAssistantType,
  TimeLineDTOType,
  TimelineOnSearchEventSrcObj,
} from '@types'

import { getRSqlBySearch } from '../../../helpers/SAFiltersToRSQL'

import { useColumns } from './useColumns'
import { useGetDataByMainObjectX } from './useGetDataByMainObjectX'
import { useGetDataByMainObjectY } from './useGetDataByMainObjectY'
import { useGetTotals } from './useGetTotals'
import { useHandlers } from './useHandlers'
import { useObjectXSubscribeWS } from './useObjectXSubscribeWS'

type UseTimeLineGridParams = {
  config: TimeLineDTOType
  searchAssistantConfig?: SearchAssistantType
  apiRef?: MutableRefObject<GridApiPro> | null
  objectXInitialRestrictions: ApiRestrictionData
  objectYInitialRestrictions: ApiRestrictionData
}

export const useTimeLineGrid = (props: UseTimeLineGridParams) => {
  const { config, apiRef } = props

  const handlers = useHandlers(props)

  const {
    dateIntervals,
    timeLineGridData,
    objectXRestrictions,
    objectYRestrictions,
    isLoadingObjectXRestrictions,
    isLoadingObjectYRestrictions,
    methods,
    watchDateRange,
    objectYSAFiltersCriteria,
    objectXSAFiltersCriteria,
    objectXSARSQLFilter,
    objectXColumnsObjectSAFiltersCriteria,
    columnsObjectData,
    isUseMainObjectXBySAFilter,
  } = handlers.state

  const {
    handleResetDateIntervals,
    handleExecuteActions,
    handleChangeVariables,
    handleDeleteTimeLineObjectXEventById,
    handleChangeTimeLineGridData,
    handleAddTimeLineObjectXEvent,
    handleUpdateTimeLineObjectXEvent,
    handleDeleteDragItemForGetTotals,
  } = handlers.handlers

  const { isNeedWaitFirstLoadWithSA, cellContextRef, objectXPKField, dragItemsForGetTotals } =
    handlers.data

  const {
    pinnedRows: pinnedTotals,
    getColumnTotals,
    refreshTotals,
  } = useGetTotals({
    config,
    ...handlers,
  })

  const dataByMainObjectY = useGetDataByMainObjectY({
    config,
    refreshTotals,
    ...handlers,
  })

  const dataByMainObjectX = useGetDataByMainObjectX({
    config,
    refreshTotals,
    ...handlers,
  })

  const handleExecuteActionWithSetVariables = (data: {
    objectYSAFiltersCriteria?: POSTObjectDataFilter
    objectXSAFiltersCriteria?: POSTObjectDataFilter
    objectXColumnsObjectSAFiltersCriteria?: POSTObjectDataFilter
    eventCode: EVENT_CODE
  }) => {
    const { eventCode } = data

    handleExecuteActions({
      actions: config.objectX.actions || [],
      eventCode,
      srcObj: {
        startDate: moment(watchDateRange[0]).toISOString(),
        endDate: moment(watchDateRange[1]).toISOString(),
        objectYSAFilters: data?.objectYSAFiltersCriteria || objectYSAFiltersCriteria,
        objectXSAFilters: data?.objectXSAFiltersCriteria || objectXSAFiltersCriteria,
        objectXColumnsObjectSAFilters:
          data?.objectXColumnsObjectSAFiltersCriteria || objectXColumnsObjectSAFiltersCriteria,
        getRSqlBySearch,
      } as TimelineOnSearchEventSrcObj,
    }).then(result => {
      let variables: Record<string, GETVariableDTO> = {}

      result.map(item => {
        if (
          item.status === 'fulfilled' &&
          item.value?.actionCode === ACTION_CODE.SET_VARIABLE_VALUE
        ) {
          variables = { ...variables, ...item.value.value }
        }
      })

      handleChangeVariables(variables)
    })
  }

  useObjectXSubscribeWS({
    config,
    timeLineGridData,
    handleChangeTimeLineGridData,
    objectXPKField,
    getColumnTotals,
    handleExecuteActionWithSetVariables,
    handleDeleteTimeLineObjectXEventById,
    handleAddTimeLineObjectXEvent,
    handleUpdateTimeLineObjectXEvent,
    objectXSARSQLFilter,
    objectYSAFiltersCriteria,
    objectXColumnsObjectSAFiltersCriteria,
    columnsObjectData,
    dragItemsForGetTotals,
    handleDeleteDragItemForGetTotals,
  })

  const params =
    config.mainObject?.objectType === MAIN_OBJECT_TYPE_X || isUseMainObjectXBySAFilter
      ? dataByMainObjectX
      : dataByMainObjectY

  const totalCount = params.totalCountElements

  useEffect(() => {
    cellContextRef.current = {
      ...cellContextRef.current,
      objectYTotalCount: totalCount,
    }
  }, [totalCount])

  useEffect(() => {
    if (!isNeedWaitFirstLoadWithSA) {
      handleExecuteActionWithSetVariables({ eventCode: EVENT_CODE.AFTER_LOAD_CONFIG })
    }
  }, [])

  const columnProps = useColumns({
    config,
    dateIntervals,
    objectXRestrictions,
    objectYRestrictions,
    isLoadingObjectYRestrictions,
    isLoadingObjectXRestrictions,
    cellContextRef,
    handlers,
    columnsObjectData,
  })

  useHandleColumnDrag(
    apiRef as MutableRefObject<GridApiPro>,
    config.objectY.entityId as string,
    objectYRestrictions,
    columnProps.columns,
    columnProps.columns,
    handlers.handlers.updateObjectYRestrictions,
    undefined,
    config.objectY.columns
  )

  useHandleColumnDrag(
    apiRef as MutableRefObject<GridApiPro>,
    config.objectX.entityId as string,
    objectXRestrictions,
    columnProps.columns,
    columnProps.columns,
    handlers.handlers.updateObjectXRestrictions,
    undefined,
    config.objectX.columns
  )

  const handleFetchNextPage: MUIDataGridProps['onRowsScrollEnd'] = () => {
    params.onReadMore?.()
  }

  const handleResetDateFilters = () => {
    methods.reset()
    handleResetDateIntervals()
  }

  return {
    handleFetchNextPage,
    pinnedRows: pinnedTotals,
    ...params,
    ...columnProps,
    ...handlers,
    handlers: {
      ...handlers.handlers,
      handleResetDateFilters,
      handleExecuteActionWithSetVariables,
      refreshTotals,
    },
    cellContextRef,
  }
}
